// Days as Cases count them: in the account's time zone (the browser's when
// unset), so a due date and the secretary's jobs mean the same day.
import {
  addDays,
  type CasePlan,
  casePlanState,
  weekStartOf,
} from "@trameria/core/cases-shared";
import { getLocale, m } from "@trameria/i18n";
import { zonedParts } from "../time/format";

const pad2 = (n: number) => String(n).padStart(2, "0");

/** YYYY-MM-DD of an instant in the time zone. */
export function dateIn(at: Date | string, timeZone?: string): string {
  const p = zonedParts(new Date(at), timeZone);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

export const todayIn = (timeZone?: string, now: Date = new Date()) =>
  dateIn(now, timeZone);

/** The last second of a day in the time zone, as an ISO instant. */
export function endOfDay(date: string, timeZone?: string): string {
  const [y, mo, d] = date.split("-").map(Number);
  const guess = Date.UTC(y, mo - 1, d, 23, 59, 59);
  const p = zonedParts(new Date(guess), timeZone);
  const asUtc = Date.UTC(
    p.year,
    p.month - 1,
    p.day,
    p.hour,
    p.minute,
    p.second,
  );
  return new Date(guess - (asUtc - guess)).toISOString();
}

/** "10/14" (Japanese) or "Oct 14" for a YYYY-MM-DD. */
export function formatMonthDay(date: string): string {
  const [y, mo, d] = date.split("-").map(Number);
  if (getLocale() === "ja") return `${mo}/${d}`;
  return new Intl.DateTimeFormat(getLocale(), {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, mo - 1, d)));
}

/**
 * The short name of an Action's plan (today, tomorrow, 10/14, this week, the
 * week of 10/19, later, carried over). Null without a plan, for closed steps
 * and for waitings.
 */
export function planLabel(
  step: {
    kind: string;
    status: string;
    plan: CasePlan | null;
    someday: boolean;
  },
  today: string,
): string | null {
  const state = casePlanState(step, today);
  if (state === null || state === "inbox") return null;
  if (state === "someday") return m.cases_plan_someday();
  const plan = step.plan as CasePlan;
  if (state === "carried")
    return m.cases_plan_carried({ date: formatMonthDay(plan.on) });
  if (plan.scope === "week")
    return plan.on === weekStartOf(today)
      ? m.cases_plan_this_week()
      : m.cases_plan_week_of({ date: formatMonthDay(plan.on) });
  if (plan.on === today) return m.cases_plan_today();
  if (plan.on === addDays(today, 1)) return m.cases_plan_tomorrow();
  return formatMonthDay(plan.on);
}

export type PlanPreset = "today" | "tomorrow" | "week" | "someday";

export function planOfPreset(
  preset: PlanPreset,
  today: string,
): CasePlan | "someday" {
  switch (preset) {
    case "today":
      return { scope: "day", on: today };
    case "tomorrow":
      return { scope: "day", on: addDays(today, 1) };
    case "week":
      return { scope: "week", on: weekStartOf(today) };
    case "someday":
      return "someday";
  }
}

/** Which preset the step's plan is now ("date" for any other day). */
export function presetOf(
  step: { plan: CasePlan | null; someday: boolean },
  today: string,
): PlanPreset | "date" | null {
  if (step.someday) return "someday";
  const plan = step.plan;
  if (!plan) return null;
  if (plan.scope === "week")
    return plan.on === weekStartOf(today) ? "week" : "date";
  if (plan.on === today) return "today";
  if (plan.on === addDays(today, 1)) return "tomorrow";
  return "date";
}

/** Whether a waiting is past its date or its target was dropped (your turn again). */
export function isOverdue(
  step: {
    kind: string;
    waitUntil: string | null;
    waitTargetDroppedAt: string | null;
  },
  now: number,
): boolean {
  return (
    step.kind === "waiting" &&
    (step.waitTargetDroppedAt !== null ||
      (step.waitUntil !== null && Date.parse(step.waitUntil) <= now))
  );
}
